import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  StatsContainer,
  StatsContent,
  StatsHeader,
  StatsTitle,
  StatsYear,
  StatsGrid,
  StatCard,
  StatCardHeader,
  StatCardIcon,
  StatCardValue,
  StatCardLabel,
  StatCardPercentage,
  ProgressSection,
  ProgressHeader,
  ProgressTitle,
  ProgressPercentage,
  ProgressBarContainer,
  ProgressBarFill,
  ProgressBarText,
  StatusBreakdown,
  StatusItem,
  StatusDot,
  StatusInfo,
  StatusLabel,
  StatusCount,
  AlertSection,
  AlertCard,
  AlertIcon,
  AlertContent,
  AlertTitle,
  AlertText,
  QuickActionButton,
} from "./evaluationStats.styled";
import {
  EVAL_STATUS_PENDING,
  EVAL_STATUS_DRAFT,
  EVAL_STATUS_CONFIRM,
  EVAL_STATUS_TO_AUTO,
  EVAL_STATUS_AUTO_EVAL,
  EVAL_STATUS_TO_EVAL,
  EVAL_STATUS_EVALUATED,
  EVAL_STATUS_NOTIFIED,
  EVAL_STATUS_CALIBRATED,
} from "../../../../../constants/index";

const EvaluationStats = ({ data }) => {
  const { t } = useTranslation();

  // Calcular estadísticas
  const stats = useMemo(() => {
    if (!data || !data.evaluations) return null;

    const evaluations = Object.values(data.evaluations);
    const total = evaluations.length;

    // Contar por estado
    const statusCounts = {
      pending: 0,
      draft: 0,
      confirm: 0,
      toAuto: 0,
      autoEval: 0,
      toEval: 0,
      evaluated: 0,
      notified: 0,
      calibrated: 0,
    };

    evaluations.forEach((evaluation) => {
      switch (evaluation.status) {
        case EVAL_STATUS_PENDING.code:
          statusCounts.pending++;
          break;
        case EVAL_STATUS_DRAFT.code:
          statusCounts.draft++;
          break;
        case EVAL_STATUS_CONFIRM.code:
          statusCounts.confirm++;
          break;
        case EVAL_STATUS_TO_AUTO.code:
          statusCounts.toAuto++;
          break;
        case EVAL_STATUS_AUTO_EVAL.code:
          statusCounts.autoEval++;
          break;
        case EVAL_STATUS_TO_EVAL.code:
          statusCounts.toEval++;
          break;
        case EVAL_STATUS_EVALUATED.code:
          statusCounts.evaluated++;
          break;
        case EVAL_STATUS_NOTIFIED.code:
          statusCounts.notified++;
          break;
        case EVAL_STATUS_CALIBRATED.code:
          statusCounts.calibrated++;
          break;
        default:
          break;
      }
    });

    // Calcular completadas (solo notificadas)
    const completed = statusCounts.notified;
    const completionPercentage =
      total > 0 ? ((completed / total) * 100).toFixed(1) : 0;

    // Calcular pendientes de acción (pending, draft, confirm, toEval, calibrated)
    const needsAction =
      statusCounts.pending +
      statusCounts.draft +
      // statusCounts.confirm +
      statusCounts.toEval +
      statusCounts.calibrated;

    // Calcular en proceso (todos excepto pending, archived, y notified)
    const inProgress =
      statusCounts.confirm +
      statusCounts.draft +
      statusCounts.calibrated +
      statusCounts.toAuto +
      statusCounts.autoEval +
      statusCounts.evaluated +
      statusCounts.toEval;

    return {
      total,
      statusCounts,
      completed,
      completionPercentage,
      needsAction,
      inProgress,
    };
  }, [data]);

  if (!stats) return null;

  const statusConfig = [
    {
      key: "pending",
      label: t("forms.evaluation.status.pending"),
      color: "#9e9e9e",
      icon: "fa-solid fa-hourglass-half",
    },
    {
      key: "draft",
      label: t("forms.evaluation.status.draft"),
      color: "#2196f3",
      icon: "fa-solid fa-file-pen",
    },
    {
      key: "confirm",
      label: t("forms.evaluation.status.confirm"),
      color: "#03a9f4",
      icon: "fa-solid fa-clipboard-check",
    },
    {
      key: "toAuto",
      label: t("forms.evaluation.status.toAuto"),
      color: "#ff9800",
      icon: "fa-solid fa-user-pen",
    },
    {
      key: "autoEval",
      label: t("forms.evaluation.status.autoEval"),
      color: "#ffc107",
      icon: "fa-solid fa-user-check",
    },
    {
      key: "toEval",
      label: t("forms.evaluation.status.toEval"),
      color: "#ff5722",
      icon: "fa-solid fa-user-tie",
    },
    {
      key: "evaluated",
      label: t("forms.evaluation.status.evaluated"),
      color: "#4caf50",
      icon: "fa-solid fa-flag-checkered",
    },
    {
      key: "notified",
      label: t("forms.evaluation.status.notified"),
      color: "#8bc34a",
      icon: "fa-solid fa-envelope-circle-check",
    },
  ];

  return (
    <>
      <StatsHeader>
        <StatsTitle>
          <i className="fa-solid fa-chart-line" />
          {t("evaluations")}
        </StatsTitle>
        <StatsYear>
          <i className="fa-solid fa-calendar" /> {data.year}
        </StatsYear>
      </StatsHeader>

      {/* Tarjetas de estadísticas principales */}
      <StatsGrid>
        <StatCard $color="#4caf50">
          <StatCardHeader>
            <StatCardIcon $color="#4caf50">
              <i className="fa-solid fa-check-double" />
            </StatCardIcon>
            <StatCardValue>{stats.completed}</StatCardValue>
          </StatCardHeader>
          <StatCardLabel>
            {t("forms.evaluation.labels.completedEvaluations")}
          </StatCardLabel>
          <StatCardPercentage $color="#4caf50">
            <i className="fa-solid fa-arrow-up" />
            {stats.completionPercentage}% {t("forms.evaluation.labels.ofTotal")}
          </StatCardPercentage>
        </StatCard>

        <StatCard $color="#ff5722">
          <StatCardHeader>
            <StatCardIcon $color="#ff5722">
              <i className="fa-solid fa-exclamation-circle" />
            </StatCardIcon>
            <StatCardValue>{stats.needsAction}</StatCardValue>
          </StatCardHeader>
          <StatCardLabel>
            {t("forms.evaluation.labels.needsAction")}
          </StatCardLabel>
          <StatCardPercentage $color="#ff5722">
            <i className="fa-solid fa-clock" />
            {((stats.needsAction / stats.total) * 100).toFixed(1)}%{" "}
            {t("forms.evaluation.labels.ofTotal")}
          </StatCardPercentage>
        </StatCard>

        <StatCard $color="#ff9800">
          <StatCardHeader>
            <StatCardIcon $color="#ff9800">
              <i className="fa-solid fa-spinner" />
            </StatCardIcon>
            <StatCardValue>{stats.inProgress}</StatCardValue>
          </StatCardHeader>
          <StatCardLabel>
            {t("forms.evaluation.labels.inProgress")}
          </StatCardLabel>
          <StatCardPercentage $color="#ff9800">
            <i className="fa-solid fa-arrow-right" />
            {((stats.inProgress / stats.total) * 100).toFixed(1)}%{" "}
            {t("forms.evaluation.labels.ofTotal")}
          </StatCardPercentage>
        </StatCard>

        <StatCard $color="#203b34">
          <StatCardHeader>
            <StatCardIcon $color="#203b34">
              <i className="fa-solid fa-users" />
            </StatCardIcon>
            <StatCardValue>{stats.total}</StatCardValue>
          </StatCardHeader>
          <StatCardLabel>
            {t("forms.evaluation.labels.totalEvaluations")}
          </StatCardLabel>
          <StatCardPercentage $color="#203b34">
            <i className="fa-solid fa-equals" />
            100% {t("forms.evaluation.labels.total")}
          </StatCardPercentage>
        </StatCard>
      </StatsGrid>

      {/* Barra de progreso general */}
      <ProgressSection>
        <ProgressHeader>
          <ProgressTitle>
            <i className="fa-solid fa-trophy" />
            {t("forms.evaluation.labels.overallProgress")}
          </ProgressTitle>
          <ProgressPercentage>{stats.completionPercentage}%</ProgressPercentage>
        </ProgressHeader>

        {/* Desglose por estado */}
        <StatusBreakdown>
          {statusConfig.map((status) => {
            const count = stats.statusCounts[status.key];
            if (count === 0) return null;
            return (
              <StatusItem key={status.key} $color={status.color}>
                <StatusDot $color={status.color} />
                <StatusInfo>
                  <StatusLabel>{status.label}</StatusLabel>
                  <StatusCount>{count}</StatusCount>
                </StatusInfo>
              </StatusItem>
            );
          })}
        </StatusBreakdown>
      </ProgressSection>

      {/* Alertas y acciones rápidas */}
      {(stats.statusCounts.toEval > 0 || stats.statusCounts.pending > 0) && (
        <AlertSection>
          {stats.statusCounts.toEval > 0 && (
            <AlertCard $type="warning">
              <AlertIcon $type="warning">
                <i className="fa-solid fa-user-tie" />
              </AlertIcon>
              <AlertContent>
                <AlertTitle>
                  {t("forms.evaluation.alerts.pendingEvaluations")}
                </AlertTitle>
                <AlertText>
                  {stats.statusCounts.toEval}{" "}
                  {t("forms.evaluation.alerts.evaluationsWaiting")}
                </AlertText>
              </AlertContent>
              {/* <QuickActionButton $type="warning">
                <i className="fa-solid fa-arrow-right" />{" "}
                {t("forms.evaluation.buttons.evaluate")}
              </QuickActionButton> */}
            </AlertCard>
          )}

          {/* {stats.statusCounts.pending > 0 && (
            <AlertCard $type="info">
              <AlertIcon $type="info">
                <i className="fa-solid fa-plus-circle" />
              </AlertIcon>
              <AlertContent>
                <AlertTitle>
                  {t("forms.evaluation.alerts.pendingCreation")}
                </AlertTitle>
                <AlertText>
                  {stats.statusCounts.pending}{" "}
                  {t("forms.evaluation.alerts.evaluationsToCreate")}
                </AlertText>
              </AlertContent>
              <QuickActionButton $type="info">
                <i className="fa-solid fa-arrow-right" />{" "}
                {t("forms.evaluation.buttons.create")}
              </QuickActionButton>
            </AlertCard>
          )} */}
        </AlertSection>
      )}
    </>
  );
};

EvaluationStats.propTypes = {
  data: PropTypes.object.isRequired,
};

export default EvaluationStats;
